import styled from "styled-components";

interface WrapperProps {
  $allowMobileOverflow: boolean;
  $backgroundColor?: string;
  $backgroundPosition: string;
  $backgroundRepeat: string;
  $backgroundSize: string;
  $brand: string;
  $desktopBackgroundImageUrl?: string;
  $hideOnDesktop: boolean;
  $hideOnMobile: boolean;
  $isInPLPGrid: boolean;
  $marginBottom?: number;
  $marginTop?: number;
  $mobileBackgroundImageUrl?: string;
  $paddingBottom?: number;
  $paddingTop?: number;
  $containChildComponents: boolean;
  $scaleBackgroundOnHover?: boolean;
  $theme: string;
  $useBackgroundOverlay?: boolean;
  $useFullWidthLayout: boolean;
}

export const Wrapper = styled.div<WrapperProps>`
  position: relative;
  overflow: hidden;
  width: ${({ $useFullWidthLayout }) => $useFullWidthLayout ? '100%' : 'auto'};

  ${({ $containChildComponents, $useFullWidthLayout }) => $containChildComponents && !$useFullWidthLayout && `
    .swiper {
      overflow: visible !important;
    }
  `}

  ${({ $hideOnDesktop }) => $hideOnDesktop && `
    @media (min-width: 768px) {
      display: none;
    }
  `}

  ${({ $hideOnMobile }) => $hideOnMobile && `
    display: none;

    @media (min-width: 768px) {
      display: block;
    }
  `}

  ${({ $theme }) => $theme == 'dark' && `
    background-color: #121212;
    color: #fff;

    h1, h2, h3, h4, h5, p {
      color: #fff;
    }
  `}

  ${({ $backgroundColor }) => $backgroundColor && `
    background-color: #${$backgroundColor};
  `}

  ${({ $scaleBackgroundOnHover }) =>
    $scaleBackgroundOnHover &&
    `
      background-color: transparent;

      &:hover .bg-image {
        transition: transform 0.5s ease;
        transform: scale(1.1);
      }
    `}

  // Desktop background image when scale on hover is off
  ${({ $desktopBackgroundImageUrl, $scaleBackgroundOnHover, $backgroundPosition, $backgroundSize, $backgroundRepeat }) =>
    $desktopBackgroundImageUrl && !$scaleBackgroundOnHover
      ? `
        background-image: url(${$desktopBackgroundImageUrl});
        background-repeat: ${$backgroundRepeat};
        background-position: ${$backgroundPosition};
        background-size: ${$backgroundSize};
      `
      : ''}

  // Mobile background image when scale on hover is off
  ${({ $mobileBackgroundImageUrl, $scaleBackgroundOnHover, $backgroundPosition, $backgroundSize, $backgroundRepeat }) =>
    $mobileBackgroundImageUrl && !$scaleBackgroundOnHover && `
      @media screen and (max-width: 768px) {
        background-image: url(${$mobileBackgroundImageUrl});
        background-repeat: ${$backgroundRepeat};
        background-position: ${$backgroundPosition};
        background-size: ${$backgroundSize};
      }
    `}

  ${({ $paddingTop }) => $paddingTop != undefined && `
    padding-top: ${$paddingTop}px;
  `}

  ${({ $paddingBottom }) => $paddingBottom != undefined && `
    padding-bottom: ${$paddingBottom}px;
  `}

  ${({ $marginTop }) => $marginTop != undefined && `
    margin-top: ${$marginTop}px;
  `}

  ${({ $marginBottom }) => $marginBottom != undefined && `
    margin-bottom: ${$marginBottom}px;
  `}

  ${({ $useBackgroundOverlay, $theme }) => $useBackgroundOverlay && `
    &:after {
      content: "";
      position: absolute;
      height: 100%;
      width: 100%;
      background: ${$theme == 'dark' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.5)'};
      left: 0px;
      top: 0px;
      z-index: 1;
      pointer-events: none;
    }
  `}

  ${({ $allowMobileOverflow }) => $allowMobileOverflow && `
    @media (max-width: 768px) {
      overflow-x: scroll;

      &::-webkit-scrollbar {
        width: 0;
        height: 0;
      }
    }
  `}

  ${({ $containChildComponents }) => $containChildComponents && `
    padding-left: 16px;
    padding-right: 16px;
  `}

  ${({ $isInPLPGrid }) => $isInPLPGrid && `
    height: 100%;
    min-height: 375px;
  `}

  ${({ $brand }) => $brand && $brand == 'fender' && `
    @media (min-width: 768px) {
      padding-left: 0px;
      padding-right: 0px;
    }
  `}
`;

interface ContainerProps {
  $brand: string;
  $containChildComponents: boolean;
  $contentContainerMaxWidth?: number;
  $useFullWidthLayout: boolean;
  $useBackgroundOverlay?: boolean;
}

export const Container = styled.div<ContainerProps>`
  position: relative;

  width: ${({ $useFullWidthLayout }) => $useFullWidthLayout ? '100%' : 'auto'};
  max-width: ${({ $useFullWidthLayout }) => $useFullWidthLayout ? '100%' : 'auto'};

  @media (min-width: 768px) {
    width: ${({ $useFullWidthLayout }) => $useFullWidthLayout ? '100%' : 'auto'};
    max-width: ${({ $useFullWidthLayout }) => $useFullWidthLayout ? '100%' : 'auto'};
  }

  ${({ $useBackgroundOverlay }) => $useBackgroundOverlay && `
    z-index: 2;
  `}

  ${({ $containChildComponents }) => $containChildComponents && `
    margin: 0 auto;
  `}

  ${({ $containChildComponents }) => $containChildComponents && `
      @media (min-width: 768px) {
        max-width: 90%;
        margin-left: auto;
        margin-right: auto;
      }
  `}

  ${({ $containChildComponents, $contentContainerMaxWidth }) => ($containChildComponents && $contentContainerMaxWidth) && `
    @media (min-width: 768px) {
      max-width: ${$contentContainerMaxWidth}px;
    }
  `}

  p {
    font-size: 1rem;
  }

  h1, h2, h3, h4, h5 {
    font-weight: 600;
  }

  h1 {
    font-size: 2.5rem;
    line-height: 3.125rem;
    @media (min-width: 768px) {
      font-size: 4rem;
      line-height: 5rem;
    }
  }

  h2 {
    font-size: 2rem;
    line-height: 2.5rem;
    @media (min-width: 768px) {
      font-size: 2.75rem;
      line-height: 3.44rem;
    }
  }

  h3 {
    font-size: 1.5rem;
    line-height: 1.875rem;
    @media (min-width: 768px) {
      font-size: 2rem;
      line-height: 2.5rem;
    }
  }

  h4 {
    font-size: 1.125rem;
    line-height: 1.4rem;
    @media (min-width: 768px) {
      font-size: 1.25rem;
      line-height: 1.56rem;
    }
  }

  // Temporary fix for SHOP-6855 - Fender Fonts - Heading Updates
  ${({ $brand }) => $brand && $brand == 'fender' && `
    h1, h2, h3, h4, h5, h6 {
      font-weight: 600;
      letter-spacing: -0.02em;

      @media (min-width: 768px) {
        font-weight: 600;
        letter-spacing: -0.02em;
      }
    }
  `}
`;

interface BackgroundImageProps {
  $desktopImageUrl: string;
  $mobileImageUrl?: string;
  $backgroundPosition: string;
  $backgroundSize: string;
  $backgroundRepeat: string;
}

export const BackgroundImage = styled.div<BackgroundImageProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${({ $desktopImageUrl }) => $desktopImageUrl});
  background-position: ${({ $backgroundPosition }) => $backgroundPosition};
  background-size: ${({ $backgroundSize }) => $backgroundSize};
  background-repeat: ${({ $backgroundRepeat }) => $backgroundRepeat};
  z-index: -1;
  pointer-events: none;
  transition: all 1000ms ease;

  @media (max-width: 768px) {
    background-image: url(${({ $mobileImageUrl, $desktopImageUrl }) => $mobileImageUrl || $desktopImageUrl});
  }
`;

export const Video = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
`;

export const FooterContainer = styled.div<{ $footerPosition?: string; $paddingTop?: number; $isInPLPGrid: boolean; }>`
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-wrap: wrap;
  padding: ${({ $paddingTop }) => $paddingTop ? `${$paddingTop}px 16px 16px 16px` : '0px 16px 16px 16px'};
  z-index: 10;

  @media (min-width: 1024px) {
    flex-direction: row;
    gap: 16px;
    justify-content: ${({ $footerPosition }) => ($footerPosition == 'Left' ? 'flex-start' : $footerPosition == 'Right' ? 'flex-end' : 'center')};
    align-items: center;
  }

  ${({ $isInPLPGrid, $footerPosition }) => $isInPLPGrid && `
    justify-content: flex-end;
    align-items: ${($footerPosition == 'Left' ? 'flex-start' : $footerPosition == 'Right' ? 'flex-end' : 'center')};

    @media (min-width: 1024px) {
      align-items: flex-end;
    }
  `}
`;
